import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { Link, useLocation } from "react-router-dom";

import { BRAND_ICONS_URLS } from "../../utils/Constants";
import { AppStrings } from "../../utils/constants/AppStrings";
import {
  AFFILIATE_PORTAL_SIGN_IN_ROUTE,
  isAffiliatePortalPath,
} from "../../routing/affiliatePortalRoutes";
import { isRegulatorPortalPath } from "../RegulatorPortal/regulatorPortalRoutes";
import { fetchIpWhitelistStatus } from "../../services/ipWhitelistAccessService";
import { applyResolvedI18nLanguage } from "../../utils/i18n/languagePreference";
import RebetLoader from "../CommonComponents/RebetLoader/RebetLoader";
import styles from "./IpWhitelistGate.module.css";

const IpWhitelistAccessContext = createContext({
  blockAccess: () => {},
});

export const useIpWhitelistAccess = () =>
  useContext(IpWhitelistAccessContext);

function IpAccessRestricted() {
  return (
    <main className={styles.restricted} data-testid="ip-access-restricted">
      <div className={styles.brand}>
        <img
          src={
            process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL +
            BRAND_ICONS_URLS.companylogo
          }
          alt={AppStrings.PAM_APP_NAME}
        />
        <span>{AppStrings.PAM_APP_NAME}</span>
      </div>
      <section className={styles.message}>
        <h1>{AppStrings.PAM_IP_ACCESS_RESTRICTED_TITLE}</h1>
        <p>{AppStrings.PAM_IP_ACCESS_RESTRICTED_DESCRIPTION}</p>
        <p
          className={styles.affiliatePrompt}
          data-testid="affiliate-redirect-prompt"
        >
          {AppStrings.PAM_IP_ACCESS_AFFILIATE_PROMPT}{" "}
          <Link
            className={styles.affiliateLink}
            to={AFFILIATE_PORTAL_SIGN_IN_ROUTE}
          >
            {AppStrings.PAM_IP_ACCESS_AFFILIATE_LINK}
          </Link>
          .
        </p>
      </section>
    </main>
  );
}

export default function IpWhitelistGate({ children }) {
  const location = useLocation();
  const isExternalPortalPath =
    isAffiliatePortalPath(location.pathname) ||
    isRegulatorPortalPath(location.pathname);
  const previousIsExternalPortalPath = useRef(isExternalPortalPath);
  const [accessState, setAccessState] = useState(
    isExternalPortalPath ? "allowed" : "checking",
  );
  const isReturningToStaff =
    previousIsExternalPortalPath.current && !isExternalPortalPath;
  const currentAccessState = isExternalPortalPath
    ? "allowed"
    : isReturningToStaff
      ? "checking"
      : accessState;
  const blockAccess = useCallback(() => setAccessState("blocked"), []);
  const contextValue = useMemo(() => ({ blockAccess }), [blockAccess]);

  useEffect(() => {
    previousIsExternalPortalPath.current = isExternalPortalPath;

    if (isExternalPortalPath) {
      setAccessState("allowed");
      return undefined;
    }

    let active = true;
    setAccessState("checking");
    fetchIpWhitelistStatus()
      .then(async ({ allowed, defaultLanguage }) => {
        if (!active) return;
        await applyResolvedI18nLanguage({
          localization: { default_language: defaultLanguage },
        });
        if (active) {
          setAccessState(allowed ? "allowed" : "blocked");
        }
      })
      .catch(() => {
        if (active) {
          setAccessState("blocked");
        }
      });

    return () => {
      active = false;
    };
  }, [isExternalPortalPath]);

  if (currentAccessState === "checking") {
    return (
      <div className={styles.loading} data-testid="ip-whitelist-loading">
        <RebetLoader height="32px" width="32px" />
      </div>
    );
  }

  if (currentAccessState === "blocked") {
    return <IpAccessRestricted />;
  }

  return (
    <IpWhitelistAccessContext.Provider value={contextValue}>
      {children}
    </IpWhitelistAccessContext.Provider>
  );
}

IpWhitelistGate.propTypes = {
  children: PropTypes.node.isRequired,
};
